import React from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Trophy, ArrowRight } from "lucide-react";
import SEOHead from "../components/SEOHead";

/**
 * Registration is CLOSED during the 2026 judging period.
 * This landing replaces the previous member-registration form between the
 * Phase 1 launch and the public re-opening planned for the 2027 edition.
 *
 * Headline switches automatically from "Phase 1" → "Phase 2" → "Phase 3"
 * by reading config.current_judging_phase, which is computed server-side
 * from the live judging_phase_states (NO hardcoded date pivot).
 *
 * To re-enable the registration form for 2027:
 *   1. Delete this file
 *   2. Rename RegisterPage.legacy.jsx → RegisterPage.jsx
 *   (No backend change needed — POST /api/auth/register is unchanged.)
 *
 * The "Register" link in the site nav still points to /register; this page
 * is what visitors land on.
 */
const RegisterPage = () => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0F172A] via-[#0F172A] to-[#1E293B] flex flex-col">
      <SEOHead
        title="Registration — WUMA 2026"
        description="The 2026 winners are live. Registrations for the next edition open January 20th, 2027."
      />

      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <div
          className="w-full max-w-2xl bg-slate-900/60 border border-orange-500/20 rounded-2xl shadow-[0_25px_50px_rgba(0,0,0,0.5)] p-10 md:p-14"
          data-testid="register-closed-landing"
        >
          {/* Badge */}
          <div className="flex justify-center mb-6">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-orange-500/15 border border-orange-500/40 text-orange-300 text-sm font-medium tracking-wide uppercase">
              <Trophy className="w-4 h-4" />
              WUMA 2026
            </span>
          </div>

          {/* Headline 1 — 2026 winners are live */}
          <h1
            className="text-3xl md:text-4xl lg:text-5xl font-bold text-white text-center leading-tight mb-6"
            data-testid="register-closed-headline"
          >
            <Link
              to="/winners"
              className="text-white hover:text-orange-400 transition-colors underline decoration-orange-500/60 decoration-2 underline-offset-8"
              data-testid="register-winners-link"
            >
              The 2026 winners<br />Are Live
            </Link>
          </h1>

          {/* Headline 2 — re-open date */}
          <p
            className="text-lg md:text-xl text-slate-300 text-center mb-12 leading-relaxed"
            data-testid="register-closed-reopen"
          >
            You can submit for <span className="text-orange-400 font-semibold">2027</span> from{" "}
            <span className="text-orange-400 font-semibold">January 20th</span>.
          </p>

          {/* Divider */}
          <div className="border-t border-slate-700/60 my-10" />

          {/* Already a member */}
          <div className="text-center">
            <p className="text-slate-400 text-sm uppercase tracking-widest mb-4">
              Already a Member
            </p>
            <Link to="/login" data-testid="register-closed-login-link">
              <Button
                size="lg"
                className="bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold px-8 py-6 text-base rounded-xl shadow-[0_8px_25px_rgba(249,115,22,0.3)] border-2 border-orange-500/40"
                data-testid="register-closed-login-btn"
              >
                Login
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
};

export default RegisterPage;
